import { useEffect, useMemo, useState } from 'react';
import {
  useGetSupplierPrepaymentLedgerQuery,
  useGetSupplierPrepaymentMovementsQuery,
} from '../Service/bankDocumentsService';
import { useGetBankDocumentApContractorsLookupQuery } from '../Service/apContractorsLookupApi';
import { getBankDocumentsErrorMessage } from '../Utils/bankDocumentsErrorMapping';

const DEFAULT_PAGE_SIZE = 20;

export const useSupplierPrepaymentLedgerPage = () => {
  const { apContractors, isLoading: isApContractorsLoading } =
    useGetBankDocumentApContractorsLookupQuery();
  const [selectedApContractorId, setSelectedApContractorId] = useState<number | undefined>(
    undefined
  );
  const [currency, setCurrency] = useState('PLN');
  const [pageNumber, setPageNumber] = useState(1);

  useEffect(() => {
    if (!selectedApContractorId && apContractors.length > 0) {
      setSelectedApContractorId(apContractors[0].id);
    }
  }, [apContractors, selectedApContractorId]);

  const skipQueries = !selectedApContractorId;

  const ledgerQuery = useGetSupplierPrepaymentLedgerQuery(
    {
      apContractorId: selectedApContractorId ?? 0,
      currency,
    },
    { skip: skipQueries }
  );

  const movementsQuery = useGetSupplierPrepaymentMovementsQuery(
    {
      apContractorId: selectedApContractorId ?? 0,
      currency,
      pageNumber,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    { skip: skipQueries }
  );

  const totalPages = useMemo(() => {
    const totalCount = movementsQuery.data?.totalCount ?? 0;
    return Math.max(1, Math.ceil(totalCount / DEFAULT_PAGE_SIZE));
  }, [movementsQuery.data?.totalCount]);

  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const errorMessage = useMemo(() => {
    if (ledgerQuery.error) {
      return getBankDocumentsErrorMessage(
        ledgerQuery.error,
        'Nie udało się pobrać salda prepayment dla dostawcy.'
      );
    }

    if (movementsQuery.error) {
      return getBankDocumentsErrorMessage(
        movementsQuery.error,
        'Nie udało się pobrać historii ruchów prepayment.'
      );
    }

    return null;
  }, [ledgerQuery.error, movementsQuery.error]);

  const setApContractorId = (apContractorId: number | undefined) => {
    setSelectedApContractorId(apContractorId);
    setPageNumber(1);
  };

  const setSelectedCurrency = (value: string) => {
    setCurrency(value.toUpperCase());
    setPageNumber(1);
  };

  return {
    apContractors,
    isApContractorsLoading,
    selectedApContractorId,
    setApContractorId,
    currency,
    setSelectedCurrency,
    pageNumber,
    setPageNumber,
    pageSize: DEFAULT_PAGE_SIZE,
    totalPages,
    ledger: ledgerQuery.data,
    movements: movementsQuery.data?.items ?? [],
    totalCount: movementsQuery.data?.totalCount ?? 0,
    isLoading: ledgerQuery.isLoading || movementsQuery.isLoading || isApContractorsLoading,
    isFetching: ledgerQuery.isFetching || movementsQuery.isFetching,
    errorMessage,
    refetch: async () => {
      await Promise.all([ledgerQuery.refetch(), movementsQuery.refetch()]);
    },
  };
};
